// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, X, FileText } from 'lucide-react';

import {
  Card,
  Input,
  Button,
  Select,
  InfoBanner,
  ConfirmDialog,
  workflowTableIconButtonDangerClass,
} from '../../../components/WorkflowFormPrimitives';
import { fmt } from '../../../utils/formatCurrency';
import { useCompanyContext } from '../../../context/CompanyContext';
import { getUniqueIndividualsFromForm } from './uniqueIndividuals';

export const OtherPaymentsContent = ({ formData, setFormData }) => {
  const { currentAnalysisId } = useCompanyContext();
  const uniqueIndividuals = getUniqueIndividualsFromForm(formData);
  const [data, setData] = useState(formData.otherPaymentsData || {});
  const lastIncomingOtherPayJson = useRef(null);

  useEffect(() => {
    lastIncomingOtherPayJson.current = null;
  }, [currentAnalysisId]);

  useEffect(() => {
    const incoming = formData.otherPaymentsData || {};
    const json = JSON.stringify(incoming);
    if (lastIncomingOtherPayJson.current !== json) {
      lastIncomingOtherPayJson.current = json;
      setData(incoming);
    }
  }, [formData.otherPaymentsData]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedIndividual, setSelectedIndividual] = useState('');
  const [newPayment, setNewPayment] = useState({ amount: '', note: '' });
  /** Pending delete — set when user clicks remove; cleared on cancel or after confirm. */
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const addPayment = () => {
    if (!selectedIndividual || !newPayment.amount) return;
    setData((p) => {
      const indData = p[selectedIndividual] || { payments: [] };
      const payments = [...(indData.payments || []), { ...newPayment, id: Date.now() }];
      return { ...p, [selectedIndividual]: { ...indData, payments } };
    });
    setNewPayment({ amount: '', note: '' });
    setSelectedIndividual('');
    setShowAddModal(false);
  };
  const removePayment = (indId, paymentId) => {
    setData((p) => ({
      ...p,
      [indId]: { ...p[indId], payments: (p[indId]?.payments || []).filter((pmt) => pmt.id !== paymentId) },
    }));
  };

  const requestRemovePayment = (indId, paymentId, individualName, payment) => {
    setDeleteConfirm({
      individualId: indId,
      paymentId,
      individualName,
      amountLabel: fmt(+payment.amount),
      notePreview: String(payment.note || '').trim() || null,
    });
  };

  const confirmRemovePayment = () => {
    if (!deleteConfirm) return;
    removePayment(deleteConfirm.individualId, deleteConfirm.paymentId);
    setDeleteConfirm(null);
  };
  useEffect(() => {
    setFormData((p) => ({ ...p, otherPaymentsData: data }));
  }, [data]);
  const getPaymentCount = () =>
    (Object.values(data) as { payments?: unknown[] }[]).reduce((sum, d) => sum + (d.payments?.length || 0), 0);
  const getTotalValue = (indId) => (data[indId]?.payments || []).reduce((sum, p) => sum + (+p.amount || 0), 0);
  const getGrandTotal = () => Object.keys(data).reduce((sum, indId) => sum + getTotalValue(indId), 0);

  return (
    <div className="space-y-4">
      <InfoBanner showIcon>
        <p className="text-xs leading-relaxed">
          <strong className="font-semibold">Other Payments.</strong> Use this section to capture any 280G parachute
          payment values not covered elsewhere (e.g., consulting agreements, non-compete payments, SERP benefits, etc.).
        </p>
      </InfoBanner>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-gray-600">
          {getPaymentCount()} payment(s) added {getPaymentCount() > 0 && `• Total: ${fmt(getGrandTotal())}`}
        </div>
        <Button variant="secondary" size="sm" onClick={() => setShowAddModal(true)}>
          <Plus size={16} className="mr-1 shrink-0" aria-hidden /> Add Payment
        </Button>
      </div>
      {uniqueIndividuals.map((ind) => {
        const payments = data[ind.id]?.payments || [];
        if (payments.length === 0) return null;
        return (
          <Card key={ind.id}>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h4 className="text-xs font-semibold text-gray-900">{ind.name}</h4>
              <span className="text-xs font-medium text-emerald-700 tabular-nums shrink-0">
                Total: {fmt(getTotalValue(ind.id))}
              </span>
            </div>
            <div className="space-y-2">
              {payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2.5">
                  <div className="flex min-w-0 flex-1 items-center space-x-4">
                    <span className="flex-shrink-0 text-xs font-medium text-emerald-700 tabular-nums">
                      {fmt(+payment.amount)}
                    </span>
                    {payment.note && <span className="truncate text-xs text-gray-600">{payment.note}</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => requestRemovePayment(ind.id, payment.id, ind.name, payment)}
                    className={`ml-2 ${workflowTableIconButtonDangerClass}`}
                    title="Remove payment"
                    aria-label="Remove payment"
                  >
                    <Trash2 size={18} strokeWidth={2} aria-hidden />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
      {getPaymentCount() === 0 && (
        <Card className="text-center py-8">
          <FileText size={24} className="mx-auto text-gray-300 mb-2" />
          <p className="text-xs text-gray-500">No other payments added</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => setShowAddModal(true)}>
            <Plus size={16} className="mr-1 shrink-0" aria-hidden /> Add Payment
          </Button>
        </Card>
      )}
      <ConfirmDialog
        open={!!deleteConfirm}
        title="Remove Payment"
        description={
          deleteConfirm ? `Remove this payment for ${deleteConfirm.individualName}? This cannot be undone.` : undefined
        }
        confirmLabel="Remove Payment"
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={confirmRemovePayment}
      >
        {deleteConfirm ? (
          <ul className="space-y-1.5 rounded-md border border-gray-100 bg-gray-50 px-3 py-2.5 text-xs text-gray-700">
            <li>
              <span className="text-gray-500">Amount: </span>
              <span className="font-medium tabular-nums text-emerald-700">{deleteConfirm.amountLabel}</span>
            </li>
            {deleteConfirm.notePreview ? (
              <li className="min-w-0">
                <span className="text-gray-500">Note: </span>
                <span className="break-words">{deleteConfirm.notePreview}</span>
              </li>
            ) : null}
          </ul>
        ) : null}
      </ConfirmDialog>

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowAddModal(false)}></div>
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-xs font-medium text-gray-900">Add Other Payment</h3>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={18} aria-hidden />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <Select
                label="Individual"
                value={selectedIndividual}
                onChange={(e) => setSelectedIndividual(e.target.value)}
              >
                <option value="">Select individual...</option>
                {uniqueIndividuals.map((ind) => (
                  <option key={ind.id} value={ind.id}>
                    {ind.name}
                  </option>
                ))}
              </Select>
              <Input
                label="280G Value"
                type="number"
                value={newPayment.amount}
                onChange={(e) => setNewPayment((p) => ({ ...p, amount: e.target.value }))}
                placeholder="0"
                note="Enter the present value of the payment for 280G purposes"
              />
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700">Note (describe this payment)</label>
                <textarea
                  value={newPayment.note}
                  onChange={(e) => setNewPayment((p) => ({ ...p, note: e.target.value }))}
                  placeholder="e.g., Consulting agreement, Non-compete payment..."
                  className="h-20 w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-xs focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <Button variant="secondary" size="sm" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={addPayment} disabled={!selectedIndividual || !newPayment.amount}>
                Add Payment
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
